/**
 * Gross Profit Chart Component
 * Shows cumulative gross profit (actual vs forecasted) over time
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts'
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend } from '@/components/ui/chart'
import type { ChartConfig } from '@/components/ui/chart'
import type { MonthlyData } from '@/types/ui/dashboard'
import { useFormatters } from '@/hooks/use-formatters'
import { ChartExportMenu } from './ChartExportMenu'
import { TimePeriodToggle } from './TimePeriodToggle'
import { exportToCSV, exportToExcel, exportToPNG, generateFilename } from '@/lib/utils/export-utils'
import { aggregateDataByTimePeriod, formatPeriodForExport, type TimePeriod } from '@/lib/utils/dashboard-utils'
import { useState, useMemo } from 'react'

interface GrossProfitChartProps {
  data: MonthlyData[]
  contractAmount: number
  projectedCost: number
  engagementId?: string | number
}

export function GrossProfitChart({ data, contractAmount, projectedCost, engagementId = 0 }: GrossProfitChartProps) {
  const { formatMoney, formatCurrencyShort } = useFormatters()
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('monthly')

  // Aggregate data based on selected time period
  const aggregatedData = useMemo(() => {
    return aggregateDataByTimePeriod(data, timePeriod)
  }, [data, timePeriod])

  // Calculate forecasted gross profit: linear accumulation
  const totalForecastedProfit = contractAmount - projectedCost
  const numberOfPeriods = aggregatedData.length
  const periodForecastIncrement = numberOfPeriods > 0 ? totalForecastedProfit / numberOfPeriods : 0

  // Transform data for gross profit visualization with running totals
  let cumulativeRevenue = 0
  let cumulativeCosts = 0
  const chartData = aggregatedData.map((item, index) => {
    // Calculate cumulative values up to current period (ACTUAL)
    cumulativeRevenue += item.revenue
    cumulativeCosts += item.costs
    const cumulativeGrossProfit = cumulativeRevenue - cumulativeCosts
    
    // Forecasted gross profit: linear accumulation
    const forecastedGrossProfit = (index + 1) * periodForecastIncrement
    
    return {
      period: item.period,
      // Carried for exports only (MM-DD-YYYY Period column); the axis uses `period`.
      periodDate: item.periodDate,
      grossProfit: cumulativeGrossProfit,
      forecastedGrossProfit,
    }
  })

  const chartConfig = {
    grossProfit: {
      label: "Gross Profit",
    },
    forecastedGrossProfit: {
      label: "Forecasted Gross Profit",
    },
  } satisfies ChartConfig

  const handleExportCSV = () => {
    const exportData = chartData.map(item => ({
      Period: formatPeriodForExport(item.periodDate),
      'Gross Profit': item.grossProfit,
      'Forecasted Gross Profit': item.forecastedGrossProfit,
    }))
    exportToCSV(exportData, generateFilename('GrossProfit', engagementId, 'csv'))
  }

  const handleExportExcel = () => {
    const exportData = chartData.map(item => ({
      Period: formatPeriodForExport(item.periodDate),
      'Gross Profit': item.grossProfit,
      'Forecasted Gross Profit': item.forecastedGrossProfit,
    }))
    exportToExcel(exportData, generateFilename('GrossProfit', engagementId, 'xlsx'), 'Gross Profit')
  }

  const handleExportPNG = () => {
    exportToPNG('gross-profit-chart', generateFilename('GrossProfit', engagementId, 'png'))
  }

  const hasData = aggregatedData && aggregatedData.length > 0

  return (
    <Card id="gross-profit-chart">
      <CardHeader className="py-3 px-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">Gross Profit</CardTitle>
            <CardDescription>Cumulative gross profit - actual vs forecasted</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <TimePeriodToggle value={timePeriod} onChange={setTimePeriod} />
            <ChartExportMenu 
              onExportCSV={handleExportCSV}
              onExportExcel={handleExportExcel}
              onExportPNG={handleExportPNG}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="flex items-center justify-center h-[300px] w-full">
            <p className="text-sm text-muted-foreground">No data available</p>
          </div>
        ) : (
        <ChartContainer config={chartConfig} className="h-[300px] w-full min-w-0 min-h-0">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="period" 
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              tickFormatter={formatCurrencyShort}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  valueFormatter={(value) => formatMoney(Number(value))}
                />
              }
            />
            <ChartLegend />
            <Line 
              type="monotone" 
              dataKey="grossProfit" 
              stroke="#22c55e" 
              strokeWidth={2}
              name="Gross Profit"
            />
            <Line 
              type="monotone" 
              dataKey="forecastedGrossProfit" 
              stroke="#8b5cf6" 
              strokeWidth={2}
              strokeDasharray="5 5"
              name="Forecasted Gross Profit"
            />
          </LineChart>
        </ChartContainer>
        )}
      </CardContent>
    </Card>
  )
}

